// =============================================================================
// domain/userContracts.ts — a trader's own instruments, and the rules that stop
// one of them quietly re-pricing the rest of the book.
// -----------------------------------------------------------------------------
// 🚨 THE GAP THIS CLOSES IS DATA LOSS, NOT A MISSING SETTINGS PANE.
//
// The shared `contracts` catalogue is admin-seeded and world-readable, with no
// write policy for anyone. A trader whose instrument is not on that list has
// every trade in it REJECTED AT IMPORT — `assembleImported` says so in as many
// words — with no screen in either client able to add the instrument and retry.
// Those trades are simply absent from their book, and nothing offers a fix.
//
// ── ⚠ EVERY NUMBER HERE IS LOAD-BEARING, AND NONE OF THEM ERRORS WHEN WRONG ──
//
// A contract spec is three numbers that silently rewrite P&L, R-multiples,
// MAE/MFE and every excursion figure for that instrument. A wrong $/point does
// not throw; it produces plausible trades with the wrong money in them — which
// is the exact shape of the SI/SIL defect that priced Micro Silver 5× too high.
// So the validation below refuses rather than coerces, and the editor DERIVES
// $/point instead of asking for it.
// =============================================================================

import type { ContractSpec } from './trade';

/** A user's own spec. Same shape the catalogue exposes, plus who owns it. */
export interface UserContract extends ContractSpec {
  symbol: string;
  name: string;
  exchange: string;
}

/** What the editor collects. Strings, because they come off form inputs. */
export interface UserContractDraft {
  symbol: string;
  name: string;
  exchange: string;
  /** Smallest price increment the instrument trades in — e.g. `0.25`. */
  tickSize: string;
  /** What ONE of those increments is worth — e.g. `12.50`. */
  dollarsPerTick: string;
}

/**
 * ⚠ A CAP, because this read is unpaged for the same reason the shared one is.
 * 200 instruments is far beyond any real trader's list and far below the row cap
 * that would truncate the read — and a truncated catalogue prices trades at $0.
 */
export const USER_CONTRACT_LIMIT = 200;

export const SYMBOL_MAX = 12;

/**
 * 🚨 UPPERCASE IS CORRECTNESS, NOT TIDINESS. `findContract` upper-cases the
 * broker's string before comparing, so a lowercase symbol matches nothing —
 * it would save, look right on the settings screen, and price every trade in
 * that instrument at $0 with nothing on screen saying why. The database CHECKs
 * this too; this is the half that can explain itself to a person.
 */
export function normalizeUserSymbol(raw: string | null | undefined): string {
  return String(raw ?? '').toUpperCase().trim().replace(/\s+/g, '');
}

/**
 * $/point from the pair exchanges actually publish.
 *
 * ⚠ THE EDITOR DOES NOT ASK FOR $/POINT, and that is the point. CME's spec pages
 * state a tick and what it is worth — "0.25 index points = $12.50" — and asking
 * for all three invites a set that disagrees with itself. Deriving it means the
 * two numbers a trader can actually look up are the two they type.
 *
 * Returns null when the inputs cannot produce a usable value, never 0 — see the
 * header on why 0 is the dangerous answer here.
 */
export function dollarsPerPointFrom(
  tickSize: number, dollarsPerTick: number,
): number | null {
  if (!Number.isFinite(tickSize) || tickSize <= 0) return null;
  if (!Number.isFinite(dollarsPerTick) || dollarsPerTick <= 0) return null;
  const dpp = dollarsPerTick / tickSize;
  return Number.isFinite(dpp) && dpp > 0 ? dpp : null;
}

/** `''` and whitespace are NOT numbers. `Number('')` is 0, which is the trap. */
function parseNum(raw: string): number | null {
  const s = String(raw ?? '').trim();
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

export interface ValidatedUserContract {
  ok: true;
  contract: UserContract;
}
export interface RejectedUserContract {
  ok: false;
  /** Which field to point at, so the message lands next to the cause. */
  field: 'symbol' | 'name' | 'tickSize' | 'dollarsPerTick' | 'limit';
  error: string;
}

/**
 * Turn a draft into a spec, or say exactly why it cannot be one.
 *
 * ⚠ REFUSES, never coerces. Every failure mode below has a plausible-looking
 * coercion available — an empty tick size to 0, a lowercase symbol to itself, a
 * negative value to its absolute — and each one produces a stored spec that
 * looks saved and prices wrongly for as long as it exists.
 */
export function validateUserContract(
  draft: UserContractDraft,
  opts: { existingCount?: number; editing?: boolean } = {},
): ValidatedUserContract | RejectedUserContract {
  const symbol = normalizeUserSymbol(draft.symbol);
  if (!symbol) {
    return { ok: false, field: 'symbol', error: 'Enter the instrument’s root symbol — the part before the month code, like MNQ or ES.' };
  }
  if (symbol.length > SYMBOL_MAX) {
    return { ok: false, field: 'symbol', error: `A root symbol is at most ${SYMBOL_MAX} characters. “${symbol}” looks like a full contract symbol — drop the month and year.` };
  }
  if (!/^[A-Z0-9]+$/.test(symbol)) {
    return { ok: false, field: 'symbol', error: 'A root symbol is letters and digits only — no spaces, dots or dashes. Exchange suffixes like “-CME” are stripped automatically when your file is read.' };
  }

  const name = String(draft.name ?? '').trim();
  if (!name) {
    return { ok: false, field: 'name', error: 'Give it a name you will recognise in a picker — “Micro Silver”, not “SIL”.' };
  }

  const tickSize = parseNum(draft.tickSize);
  if (tickSize === null || tickSize <= 0) {
    // ⚠ Named explicitly, because 0 is what an empty box yields and a tick size
    // of 0 is a division by zero everywhere ticks are counted.
    return { ok: false, field: 'tickSize', error: 'Tick size must be greater than zero — it is the smallest amount the price can move, like 0.25.' };
  }

  const dollarsPerTick = parseNum(draft.dollarsPerTick);
  if (dollarsPerTick === null || dollarsPerTick <= 0) {
    return { ok: false, field: 'dollarsPerTick', error: 'Tick value must be greater than zero — what one tick of movement is worth on one contract, like 12.50.' };
  }

  const dollarsPerPoint = dollarsPerPointFrom(tickSize, dollarsPerTick);
  if (dollarsPerPoint === null) {
    return { ok: false, field: 'tickSize', error: 'Those two numbers do not produce a usable value per point. Check the tick size against your exchange’s contract specs.' };
  }

  if (!opts.editing && (opts.existingCount ?? 0) >= USER_CONTRACT_LIMIT) {
    return { ok: false, field: 'limit', error: `You already have ${USER_CONTRACT_LIMIT} custom instruments, which is the limit. Delete one you no longer trade first.` };
  }

  return {
    ok: true,
    contract: {
      symbol,
      name,
      exchange: String(draft.exchange ?? '').trim().toUpperCase(),
      tickSize,
      dollarsPerTick,
      dollarsPerPoint,
    },
  };
}

/**
 * The catalogue the app measures with: the shared list, with the trader's own
 * rows laid over it.
 *
 * ⚠ OVERRIDE BY SYMBOL, and the user's row wins. That is the whole reason this
 * is an override table rather than an append: a trader whose broker prices an
 * instrument differently from our seeded row — or whose seeded row is simply
 * wrong — has no other way to correct their own book, and correcting it for
 * everyone is not on offer.
 *
 * ⚠ Returns a NEW array only when something changed. Identity is what stops the
 * whole app re-deriving on every render for the overwhelmingly common case of a
 * trader with no custom instruments at all.
 */
export function mergeContractCatalogue<T extends { symbol: string }>(
  shared: readonly T[],
  mine: readonly T[],
): readonly T[] {
  if (!mine.length) return shared;
  const overrides = new Map(mine.map((c) => [c.symbol, c]));
  const merged = shared.map((c) => overrides.get(c.symbol) ?? c);
  const seen = new Set(shared.map((c) => c.symbol));
  for (const c of mine) if (!seen.has(c.symbol)) merged.push(c);
  return merged;
}

export interface SymbolImpact {
  /** The symbol already exists in the shared list — this EDITS it, for you. */
  overrides: string | null;
  /**
   * Catalogue symbols this new one would capture trades from.
   *
   * 🚨 THE HAZARD, and it is not obvious from the form. Resolution takes the
   * LONGEST catalogue symbol that prefixes the broker's string. Adding `ESH` —
   * a trader mistaking a March contract for an instrument — makes every
   * `ESH26` fill resolve to `ESH` instead of `ES`, at whatever numbers were
   * typed. Nothing errors; the trades simply re-price.
   */
  captures: readonly string[];
}

/** What adding this symbol would change about symbols that already resolve. */
export function symbolImpact(
  symbol: string,
  catalogue: readonly { symbol: string }[],
): SymbolImpact {
  const s = normalizeUserSymbol(symbol);
  if (!s) return { overrides: null, captures: [] };
  const overrides = catalogue.some((c) => c.symbol === s) ? s : null;
  // Strict prefixes only — an exact match is an override, handled above.
  const captures = catalogue
    .map((c) => c.symbol)
    .filter((other) => other !== s && s.startsWith(other))
    .sort((a, b) => b.length - a.length);
  return { overrides, captures };
}
